import React, { useState, useEffect, useCallback } from 'react';
import { 
  Package, Plus, Edit2, Trash2, Save, X, Home, Briefcase,
  Check, AlertCircle, Loader2, Eye, EyeOff, Zap, Phone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PackageManager = ({ token, showToast }) => {
  const [packages, setPackages] = useState([]);
  const [blacknightProducts, setBlacknightProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('all');
  const [packageStats, setPackageStats] = useState(null);
  const [formData, setFormData] = useState({
    blacknight_product_id: '',
    blacknight_product_name: '',
    display_name: '',
    business_display_name: '',
    is_home: true,
    is_business: false,
    download_speed_mbps: 500,
    upload_speed_mbps: 100,
    monthly_price: 49.99,
    setup_fee: 0,
    equipment_fee: 0,
    features: [],
    voip_available: true,
    voip_price: 9.99,
    includes_speedboost: false,
    is_most_popular: false,
    active: true,
    sort_order: 0
  });
  const [newFeature, setNewFeature] = useState('');

  const loadPackages = useCallback(async () => {
    try {
      // Load packages
      const res = await fetch(`${API_URL}/api/admin/packages?token=${token}`);
      if (!res.ok) throw new Error('Failed to fetch packages');
      const data = await res.json();
      setPackages(data);
      
      // Load stats
      const statsRes = await fetch(`${API_URL}/api/admin/packages/stats?token=${token}`);
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setPackageStats(stats);
      }
      
      // Load Blacknight products for dropdown
      const productsRes = await fetch(`${API_URL}/api/admin/blacknight-products?token=${token}`);
      if (productsRes.ok) {
        const products = await productsRes.json();
        setBlacknightProducts(products);
      }
    } catch (err) {
      showToast('Failed to load packages', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, showToast]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const handleBlacknightProductSelect = (productId) => {
    const product = blacknightProducts.find(p => p.id === parseInt(productId));
    if (product) {
      setFormData({
        ...formData,
        blacknight_product_id: product.id,
        blacknight_product_name: product.name,
        download_speed_mbps: product.download,
        upload_speed_mbps: product.upload
      });
    } else {
      setFormData({
        ...formData,
        blacknight_product_id: '',
        blacknight_product_name: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.blacknight_product_id) {
      showToast('Please select a Blacknight product', 'error');
      return;
    }
    
    try {
      const url = editingId 
        ? `${API_URL}/api/admin/packages/${editingId}?token=${token}`
        : `${API_URL}/api/admin/packages?token=${token}`;
      
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          blacknight_product_id: parseInt(formData.blacknight_product_id)
        })
      });
      
      if (!res.ok) throw new Error('Failed to save package');
      
      showToast(editingId ? 'Package updated' : 'Package created');
      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadPackages();
    } catch (err) {
      showToast('Failed to save package', 'error');
    }
  };

  const handleEdit = (pkg) => {
    setFormData({
      blacknight_product_id: pkg.blacknight_product_id || '',
      blacknight_product_name: pkg.blacknight_product_name || '',
      display_name: pkg.display_name || pkg.name || '',
      business_display_name: pkg.business_display_name || '',
      is_home: pkg.is_home !== false,
      is_business: pkg.is_business || false,
      download_speed_mbps: pkg.download_speed_mbps || 500,
      upload_speed_mbps: pkg.upload_speed_mbps || 100,
      monthly_price: pkg.monthly_price || 0,
      setup_fee: pkg.setup_fee || 0,
      equipment_fee: pkg.equipment_fee || 0,
      features: pkg.features || [],
      voip_available: pkg.voip_available !== false,
      voip_price: pkg.voip_price || 9.99,
      includes_speedboost: pkg.includes_speedboost || false,
      is_most_popular: pkg.is_most_popular || false,
      active: pkg.active !== false,
      sort_order: pkg.sort_order || 0
    });
    setEditingId(pkg.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this package?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/packages/${id}?token=${token}`, {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('Failed to delete');
      showToast('Package deleted');
      loadPackages();
    } catch (err) {
      showToast('Failed to delete package', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      blacknight_product_id: '',
      blacknight_product_name: '',
      display_name: '',
      business_display_name: '',
      is_home: true,
      is_business: false,
      download_speed_mbps: 500,
      upload_speed_mbps: 100,
      monthly_price: 49.99,
      setup_fee: 0,
      equipment_fee: 0,
      features: [],
      voip_available: true,
      voip_price: 9.99,
      includes_speedboost: false,
      is_most_popular: false,
      active: true,
      sort_order: 0
    });
    setNewFeature('');
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({ ...formData, features: [...formData.features, newFeature.trim()] });
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData({ ...formData, features: formData.features.filter((_, i) => i !== index) });
  };

  const filteredPackages = packages.filter(pkg => {
    if (filter === 'all') return true;
    if (filter === 'home') return pkg.is_home;
    if (filter === 'business') return pkg.is_business;
    if (filter === 'discovered') return pkg.auto_discovered && !pkg.active;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Auto-Discovery Alert */}
      {packageStats?.discovered_pending_review > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-amber-900">New Products Discovered!</h4>
            <p className="text-sm text-amber-700 mt-1">
              {packageStats.discovered_pending_review} new Blacknight product(s) were automatically discovered from availability checks. 
              Click the "Discovered" tab to review and configure them.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Packages</h2>
          <p className="text-slate-600">Map Blacknight products to customer-facing packages</p>
        </div>
        <button
          onClick={() => { resetForm(); setEditingId(null); setShowForm(true); }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          data-testid="add-package-btn"
        >
          <Plus className="w-5 h-5" />
          Add Package
        </button>
      </div>

      {/* Filter */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        {['all', 'home', 'business', 'discovered'].map((tab) => (
          <button
            key={tab}
            onClick={() => setFilter(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition relative ${
              filter === tab ? 'bg-green-100 text-green-700' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {tab === 'all' ? 'All' : tab === 'home' ? 'Home' : tab === 'business' ? 'Business' : 'Discovered'}
            {tab === 'discovered' && packageStats?.discovered_pending_review > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center">
                {packageStats.discovered_pending_review}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">
                {editingId ? 'Edit Package' : 'Add New Package'}
              </h3>
              <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Blacknight Product Selection */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-3">Select Blacknight Product</h4>
                <select
                  value={formData.blacknight_product_id}
                  onChange={(e) => handleBlacknightProductSelect(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                >
                  <option value="">-- Select a product --</option>
                  {blacknightProducts.map((product) => (
                    <option key={product.id} value={product.id}>
                      ID {product.id}: {product.name} ({product.download}/{product.upload} Mbps)
                    </option>
                  ))}
                </select>
                {formData.blacknight_product_id && (
                  <p className="text-sm text-blue-700 mt-2">
                    Selected: <strong>{formData.blacknight_product_name}</strong> ({formData.download_speed_mbps}/{formData.upload_speed_mbps} Mbps)
                  </p>
                )}
                {blacknightProducts.length === 0 && (
                  <p className="text-sm text-amber-600 mt-2">
                    Loading products... Run an availability check to discover new products.
                  </p>
                )}
              </div>

              {/* Customer Display Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Customer Display Name</label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  placeholder="e.g., Home Fibre 500"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">This is what customers will see</p>
              </div>

              {/* Package Availability */}
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <h4 className="font-medium text-slate-800 mb-3">Available For</h4>
                <div className="flex gap-4">
                  <label className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 cursor-pointer hover:border-green-400 flex-1">
                    <input
                      type="checkbox"
                      checked={formData.is_home}
                      onChange={(e) => setFormData({ ...formData, is_home: e.target.checked })}
                      className="w-5 h-5 rounded border-slate-300 text-green-600 focus:ring-green-500"
                    />
                    <div className="flex items-center gap-2">
                      <Home className="w-5 h-5 text-blue-500" />
                      <span className="font-medium text-slate-700">Home Customers</span>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 cursor-pointer hover:border-green-400 flex-1">
                    <input
                      type="checkbox"
                      checked={formData.is_business}
                      onChange={(e) => setFormData({ ...formData, is_business: e.target.checked })}
                      className="w-5 h-5 rounded border-slate-300 text-green-600 focus:ring-green-500"
                    />
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-purple-500" />
                      <span className="font-medium text-slate-700">Business Customers</span>
                    </div>
                  </label>
                </div>
                {(!formData.is_home && !formData.is_business) && (
                  <p className="text-xs text-red-500 mt-2">Please select at least one customer type</p>
                )}
              </div>

              {/* Business Display Name (only shown if is_business is checked) */}
              {formData.is_business && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Business Display Name <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.business_display_name}
                    onChange={(e) => setFormData({ ...formData, business_display_name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    placeholder="e.g., Business Fibre 500 (leave blank to use default name)"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    If blank, business customers will see "{formData.display_name || 'the default display name'}"
                  </p>
                </div>
              )}

              {/* Speeds (read-only, from Blacknight selection) */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Download (Mbps)</label>
                  <input
                    type="number"
                    value={formData.download_speed_mbps}
                    onChange={(e) => setFormData({ ...formData, download_speed_mbps: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg bg-slate-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Upload (Mbps)</label>
                  <input
                    type="number"
                    value={formData.upload_speed_mbps}
                    onChange={(e) => setFormData({ ...formData, upload_speed_mbps: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg bg-slate-50"
                  />
                </div>
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Monthly Price (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.monthly_price}
                    onChange={(e) => setFormData({ ...formData, monthly_price: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    min="0"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Setup Fee (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.setup_fee}
                    onChange={(e) => setFormData({ ...formData, setup_fee: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Equipment Fee (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.equipment_fee}
                    onChange={(e) => setFormData({ ...formData, equipment_fee: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    min="0"
                  />
                </div>
              </div>

              {/* Sort Order & Active */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Sort Order</label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    min="0"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="w-5 h-5 rounded border-slate-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="font-medium text-slate-700">Active</span>
                  </label>
                </div>
              </div>

              {/* Package Badges & Features */}
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <h4 className="font-medium text-amber-900 mb-3">Package Badges</h4>
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center gap-3 p-3 bg-white rounded-lg border border-amber-200 cursor-pointer hover:border-amber-400">
                    <input
                      type="checkbox"
                      checked={formData.includes_speedboost}
                      onChange={(e) => setFormData({ ...formData, includes_speedboost: e.target.checked })}
                      className="w-5 h-5 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                    />
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-amber-500" />
                      <span className="font-medium text-slate-700">Includes SpeedBoost</span>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 bg-white rounded-lg border border-amber-200 cursor-pointer hover:border-amber-400">
                    <input
                      type="checkbox"
                      checked={formData.is_most_popular}
                      onChange={(e) => setFormData({ ...formData, is_most_popular: e.target.checked })}
                      className="w-5 h-5 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-lg">⭐</span>
                      <span className="font-medium text-slate-700">Most Popular</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* VoIP */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Phone className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-slate-700">VoIP Landline Add-on</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.voip_available}
                      onChange={(e) => setFormData({ ...formData, voip_available: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:ring-2 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                  </label>
                </div>
                {formData.voip_available && (
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">VoIP Price (€/month)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.voip_price}
                      onChange={(e) => setFormData({ ...formData, voip_price: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      min="0"
                    />
                  </div>
                )}
              </div>

              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Features</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg"
                    placeholder="Add a feature..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  />
                  <button type="button" onClick={addFeature} className="px-4 py-2 bg-slate-100 rounded-lg hover:bg-slate-200">
                    Add
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-lg">
                      <span className="text-sm">{feature}</span>
                      <button type="button" onClick={() => removeFeature(index)} className="text-red-500 hover:text-red-700">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="px-4 py-2 text-slate-600">
                  Cancel
                </button>
                <button type="submit" className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  {editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Packages List */}
      {filteredPackages.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl">
          <Package className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No Packages</h3>
          <p className="text-slate-600">Create your first package to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPackages.map((pkg) => (
            <div 
              key={pkg.id} 
              className={`bg-white rounded-xl border p-5 ${pkg.active ? 'border-slate-200' : 'border-red-200 bg-red-50'}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-slate-900">{pkg.display_name || pkg.name}</h3>
                  {pkg.business_display_name && pkg.is_business && (
                    <p className="text-xs text-purple-600">Business: {pkg.business_display_name}</p>
                  )}
                  <p className="text-xs text-slate-500">ID: {pkg.blacknight_product_id}</p>
                </div>
                <div className="flex flex-col gap-1">
                  {pkg.is_home && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                      Home
                    </span>
                  )}
                  {pkg.is_business && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                      Business
                    </span>
                  )}
                </div>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-1 mb-2">
                {pkg.auto_discovered && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                    🔍 Auto-Discovered
                  </span>
                )}
                {pkg.includes_speedboost && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                    <Zap className="w-3 h-3" /> SpeedBoost
                  </span>
                )}
                {pkg.is_most_popular && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                    ⭐ Popular
                  </span>
                )}
              </div>

              <div className="text-sm text-slate-600 mb-2">
                {pkg.download_speed_mbps}/{pkg.upload_speed_mbps} Mbps
              </div>

              <div className="text-2xl font-bold text-slate-900 mb-1">
                €{pkg.monthly_price?.toFixed(2)}<span className="text-sm font-normal text-slate-500">/mo</span>
              </div>

              {(pkg.setup_fee > 0 || pkg.equipment_fee > 0) && (
                <div className="text-xs text-slate-500 mb-2">
                  {pkg.setup_fee > 0 && <span>Setup: €{pkg.setup_fee?.toFixed(2)} </span>}
                  {pkg.equipment_fee > 0 && <span>Equipment: €{pkg.equipment_fee?.toFixed(2)}</span>}
                </div>
              )}

              <p className="text-xs text-slate-500 mb-3 truncate" title={pkg.blacknight_product_name}>
                {pkg.blacknight_product_name}
                {pkg.provider && <span className="text-blue-600"> ({pkg.provider})</span>}
              </p>

              {pkg.auto_discovered && !pkg.active && (
                <div className="bg-blue-50 rounded-lg p-2 mb-3 text-xs">
                  <p className="text-blue-700 font-medium">⚠️ Needs configuration</p>
                  <p className="text-blue-600">Set display name, pricing, and activate to make available to customers.</p>
                </div>
              )}

              {!pkg.active && !pkg.auto_discovered && (
                <div className="text-xs text-red-600 mb-3 flex items-center gap-1">
                  <EyeOff className="w-3 h-3" /> Inactive
                </div>
              )}

              <div className="flex gap-2 pt-3 border-t border-slate-100">
                <button
                  onClick={() => handleEdit(pkg)}
                  className="flex-1 px-3 py-2 text-sm text-green-600 hover:bg-green-50 rounded-lg transition flex items-center justify-center gap-1"
                >
                  <Edit2 className="w-4 h-4" /> Edit
                </button>
                <button
                  onClick={() => handleDelete(pkg.id)}
                  className="flex-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition flex items-center justify-center gap-1"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PackageManager;
